import { ClerkProvider } from "@clerk/clerk-react";
import { QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BootFailureScreen } from "@/components/layout/boot-failure-screen";
import { createQueryClient } from "@/lib/api/query-client";
import { clerkAppearance } from "@/lib/clerk-appearance";
import { EnvValidationError, loadEnv } from "@/lib/env";
import { initSentry } from "@/lib/sentry";
import { router } from "@/router";
import "./index.css";

const rootElement = document.getElementById("root");
if (!rootElement) {
  throw new Error("Root element not found.");
}
const root = createRoot(rootElement);

try {
  const env = loadEnv();

  initSentry({
    ...(env.sentryDsn ? { dsn: env.sentryDsn } : {}),
    release: env.sentryRelease,
    environment: env.isProd ? "production" : "development",
  });

  const queryClient = createQueryClient(() => {
    void router.navigate({ to: "/sign-in" });
  });

  root.render(
    <StrictMode>
      <ClerkProvider publishableKey={env.clerkPublishableKey} appearance={clerkAppearance}>
        <QueryClientProvider client={queryClient}>
          <RouterProvider router={router} />
        </QueryClientProvider>
      </ClerkProvider>
    </StrictMode>,
  );
} catch (error) {
  if (error instanceof EnvValidationError) {
    // The honest boot-failure state (TASK.md): never a blank page, never a silent default.
    root.render(
      <StrictMode>
        <BootFailureScreen fields={error.fields} />
      </StrictMode>,
    );
  } else {
    throw error;
  }
}
